import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService, dataPlaneService } from "../dataMapperServices";
import { refusalReason, sameId, type DataMapperProfile } from "../dataMapper.types";
import { subscribeTabEvents } from "../tabEvents";
import {
  BULK_EXPORT_EVENTS,
  isFinishedStatus,
  type BulkExportEvent,
  type BulkExportEventName,
  type BulkExportMappingResult,
  type BulkExportPhase,
  type BulkExportPlanAnswer,
  type BulkExportPlanEntry,
  type BulkExportStatus,
  type BulkExportTableResult,
  type BulkExportView,
} from "./bulkExport.types";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { bearerHeaders, saveBlobInSession } from "shared/sessionDownload";
import { planCounts, type PlanCounts } from "./bulkExport.format";

export const DATA_MAPPER_BULK_EXPORT_ROUTE = "Data Mapper Bulk Export";

/** How often a running export is read while waiting for its events (the fallback for a missed one). */
export const BULK_EXPORT_POLL_MS = 3000;
/** After a Download: up to ten reads 300 ms apart (~3 s) until the API has deleted the zip. */
export const DOWNLOAD_SETTLE_READS = 10;
export const DOWNLOAD_SETTLE_MS = 300;

export const NOT_TITAN_MESSAGE =
  "Bulk Export is only for profiles marked as Titan profiles. Mark this profile as Titan on the Profiles page if its source is a Titan database.";

/** A Bulk Export refusal as the sentence the screen shows. */
export function bulkExportErrorMessage(err: unknown, fallback: string): string {
  const reason = refusalReason(err) ?? (err as { reason?: unknown } | null)?.reason;
  if (reason === "not-a-titan-profile") return NOT_TITAN_MESSAGE;
  if (reason === "not-connected" || reason === "selection-mismatch") {
    return "This tab has no Titan source open for this profile. Connect one on the Data Source screen first.";
  }
  if (reason === "download-in-progress") {
    return "This export is already being downloaded (in another tab?). Wait for that download to finish.";
  }
  if (reason === "export-not-found") {
    return "HQ no longer has this export's download: it was taken, discarded or expired (downloads are deleted after a short time).";
  }
  const message = (err as { message?: unknown } | null)?.message;
  if (typeof message === "string" && message && !(err instanceof Error)) return message;
  return loadErrorMessage(err, fallback);
}

/** Replace earlier results for the same output file instead of appending duplicates (the source tool's merge). */
export function mergeByOutputFile<T extends { outputFile: string }>(existing: T[], incoming: T[]): T[] {
  const keys = new Set(incoming.map((r) => r.outputFile));
  return [...existing.filter((r) => !keys.has(r.outputFile)), ...incoming];
}


/**
 * Data Mapper → Bulk Export, for a profile marked as a Titan profile: the plan
 * of every mapped Titan table, auto-generated mappings, and the export itself,
 * which runs on the server in the background with live progress and ends in ONE
 * download: a zip of the JSON files and EXPORT-REPORT.md. HQ holds the zip only
 * until it is downloaded, or a short time after the run (the API's
 * DATA_MAPPER_BULK_EXPORT_TTL_MINUTES), and only the person who ran it can take it.
 * Ported from the standalone tool's BulkExportStore / BulkExportPanel.
 *
 * WHAT IT HOLDS. The plan, the mapping results and the run's status are the
 * API's answers for this page (no HQ entity, no shared cache), like the Import
 * screen's report. The profile stays in its ServiceStore; its `titanSource`
 * flag is read from there.
 */
class DataMapperBulkExportStore {
  rootStore: RootStore;

  planLoading = false;
  plan: BulkExportPlanAnswer | null = null;
  planError: string | null = null;

  mappingBusy: number | "all" | null = null;
  mappingResults: BulkExportMappingResult[] = [];
  mappingError: string | null = null;

  starting = false;
  startError: string | null = null;
  exportId: string | null = null;
  dryRun = false;
  status: BulkExportStatus | null = null;
  phase: BulkExportPhase | null = null;
  tableIndex = 0;
  tableCount = 0;
  currentTable: string | null = null;
  rows = 0;
  totalRows = 0;
  view: BulkExportView | null = null;
  exportError: string | null = null;

  downloading = false;
  downloadError: string | null = null;
  discarding = false;

  private unsubscribe: (() => void) | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  /** Re-reads after a Download, until the run stops saying the zip is ready. */
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  /** One read just after a ready zip's expiry, so a page left open learns it expired. */
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  private get service() {
    return dataPlaneService(this.rootStore, "data-mapper-bulk-export");
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get tabId(): string | null {
    return this.selection?.tabId ?? null;
  }

  get profile(): DataMapperProfile | null {
    const id = this.profileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ??
      []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  /** The open profile is marked as a Titan profile (the API checks it again on every call). */
  get isTitanProfile(): boolean {
    return this.profile?.titanSource === true;
  }

  /** Titan tables and export files, counted apart (bulkExport.format.ts planCounts). */
  get planCounts(): PlanCounts {
    return planCounts(Object.values(this.plan?.batches ?? {}).flat());
  }

  get batchNumbers(): number[] {
    return Object.keys(this.plan?.batches ?? {})
      .map(Number)
      .sort((a, b) => a - b);
  }

  entriesForBatch(batch: number): BulkExportPlanEntry[] {
    return this.plan?.batches[String(batch)] ?? [];
  }

  mappingFor(outputFile: string): BulkExportMappingResult | undefined {
    return this.mappingResults.find((r) => r.outputFile === outputFile);
  }

  resultFor(outputFile: string): BulkExportTableResult | undefined {
    return this.view?.results.find((r) => r.outputFile === outputFile);
  }

  get running(): boolean {
    return this.status === "running";
  }

  get busy(): boolean {
    return this.planLoading || this.mappingBusy !== null || this.starting || this.running;
  }

  /**
   * A ready zip with no expiry time is one the API is deleting: removeOutput
   * clears expiresAt first and sets the final state only after the rm.
   */
  get finishingDownload(): boolean {
    const d = this.view?.download;
    return d?.state === "ready" && !d.expiresAt;
  }

  get canDownload(): boolean {
    const d = this.view?.download;
    return d?.state === "ready" && !!d.expiresAt && !this.downloading;
  }

  /** 0..100 over tables, or null before the first event. */
  get percent(): number | null {
    if (!this.tableCount) return null;
    return Math.min(100, Math.round((this.tableIndex / this.tableCount) * 100));
  }

  // ---- Route entry ----------------------------------------------------------

  async enter(): Promise<void> {
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    this.selection?.bindToUser(authStore?.currentUser?.id);
    this.subscribe();
    if (this.exportId && this.running) {
      void this.readExport();
      this.schedulePoll();
    }
    await this.load();
  }

  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile) return;
    try {
      await dataMapperService(this.rootStore, "data-mapper-profiles")?.find({
        query: { clientId: selection.selectedClientId ?? undefined },
      });
    } catch (err) {
      runInAction(() => {
        if (seq === this.loadSeq) { if (!isSessionEnded(err)) this.planError = loadErrorMessage(err, "The profile could not be loaded."); }
      });
      return;
    }
    if (seq !== this.loadSeq || !this.isTitanProfile) return;
    await this.generatePlan();
  }

  // ---- Plan and mappings -------------------------------------------------------

  async generatePlan(): Promise<void> {
    const service = this.service;
    const selection = this.selection;
    if (!service || !selection?.hasProfile || !this.isTitanProfile) return;
    // Each call below can outlive a sign-out. The session guard rejects it then
    // (never an answer), and `ended()` keeps this catch from writing that
    // rejection onto the store the sign-out just emptied.
    const ended = sessionMark(this.rootStore);
    this.planLoading = true;
    this.planError = null;
    try {
      const answer = (await service.create({ action: "generate-plan", ...selection.dataPlaneQuery })) as BulkExportPlanAnswer;
      if (ended()) return;
      runInAction(() => {
        this.plan = answer;
        this.planLoading = false;
      });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.planLoading = false;
        this.planError = bulkExportErrorMessage(err, "The export plan could not be generated.");
      });
    }
  }

  /** Auto-map one batch (or every table) and save the mappings as `bulk-*` configurations on this profile. */
  async generateMappings(batch?: number): Promise<void> {
    const service = this.service;
    const selection = this.selection;
    if (!service || !selection?.hasProfile || this.busy) return;
    const ended = sessionMark(this.rootStore);
    this.mappingBusy = batch ?? "all";
    this.mappingError = null;
    try {
      const results = (await service.create({
        action: "generate-mappings",
        ...selection.dataPlaneQuery,
        ...(batch !== undefined ? { batch } : {}),
      })) as BulkExportMappingResult[];
      if (ended()) return;
      runInAction(() => {
        this.mappingResults = mergeByOutputFile(this.mappingResults, results);
        this.mappingBusy = null;
      });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.mappingBusy = null;
        this.mappingError = bulkExportErrorMessage(err, "The mappings could not be generated.");
      });
    }
  }

  // ---- The export --------------------------------------------------------------

  /** Start an export of one batch, or every table, in the background. A dry run writes nothing. */
  async startExport(opts: { batch?: number; dryRun?: boolean; withMappings?: boolean } = {}): Promise<boolean> {
    const service = this.service;
    const selection = this.selection;
    if (!service || !selection?.hasProfile || this.busy) return false;
    const withMappings = opts.withMappings === true && opts.batch !== undefined;
    const ended = sessionMark(this.rootStore);
    this.stopPolling();
    this.starting = true;
    this.startError = null;
    this.resetRun();
    this.dryRun = opts.dryRun === true;
    this.subscribe();
    try {
      const answer = (await service.create({
        action: withMappings ? "execute-batch" : "execute-export",
        ...selection.dataPlaneQuery,
        ...(opts.batch !== undefined ? { batch: opts.batch } : {}),
        ...(opts.dryRun ? { dryRun: true } : {}),
      })) as { exportId?: string; mappings?: BulkExportMappingResult[] } | null;
      // The export was started on the server, but by the person who signed out:
      // this tab must not adopt it (its id, its poll timer, its subscription).
      if (ended()) return false;
      const exportId = answer?.exportId;
      if (!exportId) throw new Error("The API did not say which export it started.");
      runInAction(() => {
        this.starting = false;
        this.exportId = exportId;
        if (answer?.mappings) this.mappingResults = mergeByOutputFile(this.mappingResults, answer.mappings);
        if (this.status === null) {
          this.status = "running";
          this.phase = "exporting";
        }
      });
      await this.readExport();
      if (ended()) return false;
      this.schedulePoll();
      return true;
    } catch (err) {
      if (ended()) return false;
      runInAction(() => {
        this.starting = false;
        this.startError = bulkExportErrorMessage(err, "The export could not be started.");
      });
      return false;
    }
  }

  private resetRun() {
    this.exportId = null;
    this.status = null;
    this.phase = null;
    this.tableIndex = 0;
    this.tableCount = 0;
    this.currentTable = null;
    this.rows = 0;
    this.totalRows = 0;
    this.view = null;
    this.exportError = null;
    this.downloadError = null;
    this.stopDownloadReads();
  }

  private subscribe() {
    if (this.unsubscribe || !this.tabId) return;
    this.unsubscribe = subscribeTabEvents<BulkExportEventName>(this.rootStore, {
      path: "data-mapper-bulk-export",
      events: [BULK_EXPORT_EVENTS.progress, BULK_EXPORT_EVENTS.complete, BULK_EXPORT_EVENTS.error],
      tabId: this.tabId,
      onEvent: (event, payload) => this.onEvent(event, payload as unknown as BulkExportEvent),
    });
  }

  /** An event for THIS tab (tabEvents dropped the others), and only for the export started here. */
  onEvent(event: BulkExportEventName, payload: BulkExportEvent) {
    if (!this.exportId || payload.exportId !== this.exportId) return;
    if (isFinishedStatus(this.status)) return;
    this.status = payload.status;
    this.phase = payload.phase;
    this.tableIndex = Number(payload.tableIndex) || 0;
    this.tableCount = Number(payload.tableCount) || 0;
    this.currentTable = payload.table;
    this.rows = Number(payload.rows) || 0;
    this.totalRows = Number(payload.totalRows) || 0;
    if (event !== BULK_EXPORT_EVENTS.progress) {
      if (payload.error) this.exportError = bulkExportErrorMessage(payload.error, payload.error.message);
      this.stopPolling();
      // The final event carries counts only: the per-table results come from a read.
      void this.readExport();
    }
  }

  applyView(view: BulkExportView) {
    if (!this.exportId || view.exportId !== this.exportId) return;
    this.view = view;
    this.status = view.status;
    this.phase = view.phase;
    this.tableIndex = view.tableIndex;
    this.tableCount = view.tableCount;
    this.currentTable = view.currentTable;
    this.rows = view.rows;
    this.totalRows = view.totalRows;
    if (view.error) this.exportError = bulkExportErrorMessage(view.error, view.error.message);
    if (isFinishedStatus(view.status)) this.stopPolling();
    this.scheduleExpiryRead();
  }

  async readExport(): Promise<void> {
    const service = this.service;
    const id = this.exportId;
    if (!service || !id) return;
    const ended = sessionMark(this.rootStore);
    try {
      const view = (await service.get(id)) as BulkExportView;
      if (ended()) return;
      runInAction(() => this.applyView(view));
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        if (id !== this.exportId) return;
        if (refusalReason(err) === "export-not-found") {
          this.stopPolling();
          if (!isFinishedStatus(this.status)) this.status = "failed";
          this.exportError = "HQ no longer has this export (the API restarted, or it finished hours ago).";
        }
      });
    }
  }

  private schedulePoll() {
    this.stopPolling();
    if (!this.exportId || isFinishedStatus(this.status)) return;
    // shortcut: a fixed-interval read while running, as the Import screen does; drop it if events prove reliable.
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      void this.readExport().then(() => this.schedulePoll());
    }, BULK_EXPORT_POLL_MS);
  }

  private stopPolling() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  /**
   * After a Download the API deletes the zip once the response has finished,
   * so the first read can still say `ready`, and no event follows. Read again
   * until it says otherwise (downloaded, or deleting when the rm failed).
   */
  private readUntilSettled(left = DOWNLOAD_SETTLE_READS) {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      void this.readExport().then(() => {
        if (this.view?.download.state === "ready" && left > 1) this.readUntilSettled(left - 1);
      });
    }, DOWNLOAD_SETTLE_MS);
  }

  /** The sweeper deletes an expired zip without an event: read once after its time. */
  private scheduleExpiryRead() {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    const d = this.view?.download;
    if (d?.state !== "ready" || !d.expiresAt) return;
    const at = new Date(d.expiresAt).getTime();
    if (!Number.isFinite(at)) return;
    // The sweeper runs at most a minute apart; setTimeout caps at ~24.8 days.
    const delay = Math.min(Math.max(0, at - Date.now()) + 60_000, 2 ** 31 - 1);
    this.expiryTimer = setTimeout(() => {
      this.expiryTimer = null;
      void this.readExport();
    }, delay);
  }

  private stopDownloadReads() {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.settleTimer = null;
    this.expiryTimer = null;
  }

  /** Cancel the running export, or delete a finished one's zip. Either way HQ keeps nothing of it. */
  async discard(): Promise<void> {
    const service = this.service;
    const id = this.exportId;
    if (!service?.remove || !id || this.discarding) return;
    const ended = sessionMark(this.rootStore);
    this.discarding = true;
    try {
      const view = (await service.remove(id)) as BulkExportView | null;
      if (ended()) return;
      runInAction(() => {
        this.discarding = false;
        if (view) this.applyView(view);
      });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.discarding = false;
        this.exportError = bulkExportErrorMessage(err, "The export could not be discarded.");
      });
    }
  }

  /**
   * Download the zip, with this person's own session (the Bearer header): the
   * API serves it to the person who ran the export and no one else, so no
   * ticket or link stands in for the session (Codex review). The bytes reach
   * the browser through a transient object URL, as the invoice PDF does; the
   * API deletes its copy once it has been sent, then the run is read again.
   */
  async download(): Promise<boolean> {
    const id = this.exportId;
    if (!id || !this.canDownload) return false;
    this.downloading = true;
    this.downloadError = null;
    // A raw fetch the session guard never sees (shared/sessionDownload.ts): a
    // sign-out while the zip is in flight must not save the previous person's
    // client data in the next person's session.
    const ended = sessionMark(this.rootStore);
    try {
      const { env } = await import("../../../config/environment");
      // Before the token is read: after a sign-out it is the next person's.
      if (ended()) return false;
      const token = (this.rootStore as unknown as { authStore?: { token?: string | null } }).authStore?.token ?? null;
      const response = await fetch(`${env.apiUrl}/data-mapper-bulk-export/${encodeURIComponent(id)}/download`, {
        headers: bearerHeaders(token),
      });
      if (ended()) return false;
      if (!response.ok) {
        let reason: unknown = null;
        let message = `The download failed (${response.status}).`;
        try {
          const body = (await response.json()) as { message?: unknown; data?: { reason?: unknown } };
          reason = body?.data?.reason ?? null;
          if (typeof body?.message === "string" && body.message) message = body.message;
        } catch {
          // Not JSON: the status line says it.
        }
        throw { message, data: { reason } };
      }
      // shortcut: the whole zip is held in the page's memory before it is saved (as invoice PDFs are);
      // past a few hundred MB, stream it to disk (File System Access API) instead.
      const blob = await response.blob();
      if (!saveBlobInSession(ended, blob, this.view?.download.fileName ?? "titan-bulk-export.zip")) return false;
      runInAction(() => {
        this.downloading = false;
      });
      this.readUntilSettled();
      return true;
    } catch (err) {
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.downloading = false;
        this.downloadError = bulkExportErrorMessage(err, "The download failed.");
      });
      return false;
    }
  }

  /** Put the finished run away, to start another. */
  clearRun() {
    if (this.running || this.starting) return;
    this.resetRun();
    this.dryRun = false;
  }

  clearErrors() {
    this.startError = null;
    this.mappingError = null;
    this.downloadError = null;
  }

  /** Route exit: stop listening and reading. A running export keeps running on the server. */
  reset() {
    this.loadSeq++;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.stopPolling();
    this.stopDownloadReads();
    this.startError = null;
    this.planError = null;
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.planLoading = false;
    this.plan = null;
    this.mappingBusy = null;
    this.mappingResults = [];
    this.mappingError = null;
    this.starting = false;
    this.exportId = null;
    this.dryRun = false;
    this.status = null;
    this.phase = null;
    this.tableIndex = 0;
    this.tableCount = 0;
    this.currentTable = null;
    this.rows = 0;
    this.totalRows = 0;
    this.view = null;
    this.exportError = null;
    this.downloading = false;
    this.downloadError = null;
    this.discarding = false;
  }
}

export default DataMapperBulkExportStore;
